
"use client";

import { useState, useEffect, useCallback, createContext, useContext, type ReactNode } from 'react';
import { useRouter } from 'next/navigation';
import { ALL_SUMMARY_CARDS_CONFIG, ALL_SHORTCUT_CARDS_CONFIG, ALL_ANALYTICS_CHARTS_CONFIG } from '@/lib/constants';
import { USER_ROLES_AVAILABLE, ROLES_CONFIG, type UserRole, type Permission, type RolesConfig } from '@/config/roles';
import type { UserPreferences, GeneralUiPreferences, AtalhosPreferences, GraficosPreferences } from '@/lib/types';
import { createSupabaseBrowserClient } from '@/lib/supabase';
import type { Session, User, AuthError, SupabaseClient } from '@supabase/supabase-js';
import { getUserPreferences, updateUserPreferences } from '@/lib/actions/user.actions';
import { saveRolesPermissions } from '@/lib/actions/roles.actions';
import { useToast } from './use-toast';
import { useIdleTimeout } from './useIdleTimeout';

interface AuthContextType {
  supabase: SupabaseClient;
  currentUser: User | null;
  currentUserRoles: UserRole[];
  realUserRoles: UserRole[];
  userPreferences: UserPreferences;
  rolesConfig: RolesConfig;
  login: (credentials: { email: string; password: string }) => Promise<{ error: AuthError | null }>;
  signUp: (credentials: { email: string; password: string }) => Promise<{ user: User | null, error: AuthError | null }>;
  logout: (options?: { silent?: boolean }) => Promise<void>;
  sendPasswordResetEmail: (email: string) => Promise<{ error: AuthError | null }>;
  updateUserPassword: (password: string) => Promise<{ error: AuthError | null }>;
  setSimulatedUserRoles: (roles: UserRole[]) => void;
  clearSimulatedUserRoles: () => void;
  updateUserPreferences: (newPreferences: Partial<UserPreferences>) => Promise<void>;
  updateRolesConfig: (newConfig: RolesConfig) => Promise<{ success: boolean; error?: string }>;
  isSimulated: boolean;
  isLoading: boolean;
  isLoadingRole: boolean;
  hasPermission: (permissionToCheck?: Permission) => boolean;
  availableRoles: Array<{value: UserRole, label: string}>;
}

const defaultPreferences: UserPreferences = {
  theme: 'light',
  dashboard: {
    summary: {
      cards: ALL_SUMMARY_CARDS_CONFIG.map(c => ({ id: c.id, visible: c.defaultVisible })),
      valuesVisible: true,
    },
    shortcuts: {
      cards: ALL_SHORTCUT_CARDS_CONFIG.map(c => ({ id: c.id, visible: c.defaultVisible })),
    },
    analytics: {
      charts: ALL_ANALYTICS_CHARTS_CONFIG.map(c => ({ id: c.id, visible: c.defaultVisible })),
    }
  },
  session: {
    idleTimeoutEnabled: true,
    idleTimeoutMinutes: 15,
  }
};


const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [supabase] = useState<SupabaseClient>(() => createSupabaseBrowserClient());
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [realUserRoles, setRealUserRoles] = useState<UserRole[]>([]);
  const [simulatedUserRoles, setSimulatedUserRoles] = useState<UserRole[] | null>(null);
  const [userPreferences, setUserPreferences] = useState<UserPreferences>(defaultPreferences);
  const [rolesConfig, setRolesConfig] = useState<RolesConfig>(ROLES_CONFIG);
  const [isLoading, setIsLoading] = useState(true); 
  const [isLoadingRole, setIsLoadingRole] = useState(true); 
  const router = useRouter();
  const { toast } = useToast();

  const currentUserRoles = simulatedUserRoles ?? realUserRoles;
  const isSimulated = simulatedUserRoles !== null;

  const logout = useCallback(async (options?: { silent?: boolean }) => {
    await supabase.auth.signOut();
    setCurrentUser(null);
    setRealUserRoles([]);
    setSimulatedUserRoles(null);
    setUserPreferences(defaultPreferences);
    
    if (!options?.silent) {
        toast({
            title: "Sessão encerrada",
            description: "Você foi desconectado.",
        });
    }
    
    router.push('/login');
  }, [supabase.auth, router, toast]);

  useIdleTimeout(
    () => logout({ silent: false }),
    userPreferences.session.idleTimeoutEnabled ? userPreferences.session.idleTimeoutMinutes * 60 * 1000 : null
  );

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const user = session?.user ?? null;
      setCurrentUser(user);
      setIsLoading(false); 

      if (_event === 'SIGNED_IN') {
        setSimulatedUserRoles(null);
      }
      if (_event === 'PASSWORD_RECOVERY') {
        router.push('/update-password');
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [router, supabase]);

  useEffect(() => {
    if (currentUser) {
      // Apenas define isLoadingRole como true no primeiro carregamento.
      // Evita o efeito "flash" e desmontagem de modais/formulários na renovação de sessão do Supabase ao focar na janela.
      if (realUserRoles.length === 0) {
        setIsLoadingRole(true);
      }
      const fetchUserData = async () => {
        try {
          // Busca o perfil do usuário logado
          const { data: profileData, error: profileError } = await supabase
              .from('user_profiles')
              .select('profile, ui_preferences, atalhos_preferences, graficos_preference')
              .eq('id', currentUser.id)
              .maybeSingle();

          if (profileError) {
              console.error('Error fetching user profile:', profileError.message);
              setRealUserRoles([]);
              setUserPreferences(defaultPreferences);
              return;
          }

          const rawProfile = profileData?.profile ?? [];
          const rolesFromDb = Array.isArray(rawProfile) ? rawProfile : [rawProfile];
          const validRoles = rolesFromDb.filter((r): r is UserRole => USER_ROLES_AVAILABLE.includes(r as any));
          setRealUserRoles(validRoles);

          const generalPrefs: Partial<GeneralUiPreferences> = profileData?.ui_preferences || {};
          const atalhosPrefs: Partial<AtalhosPreferences> = profileData?.atalhos_preferences || {};
          const graficosPrefs: Partial<GraficosPreferences> = profileData?.graficos_preference || {};

          const mergedPrefs: UserPreferences = {
              theme: generalPrefs.theme || defaultPreferences.theme,
              session: { ...defaultPreferences.session, ...generalPrefs.session },
              dashboard: {
                  summary: { ...defaultPreferences.dashboard.summary, ...atalhosPrefs.summary },
                  shortcuts: { ...defaultPreferences.dashboard.shortcuts, ...atalhosPrefs.shortcuts },
                  analytics: { ...defaultPreferences.dashboard.analytics, ...graficosPrefs.analytics },
              }
          };
          setUserPreferences(mergedPrefs);

          // Busca as permissões dinâmicas no banco
          const { data: rolesData, error: rolesError } = await supabase
              .from('roles_permissions')
              .select('role, name, description, permissions');

          if (!rolesError && rolesData && rolesData.length > 0) {
              const loadedConfig = {} as RolesConfig;
              rolesData.forEach(item => {
                  loadedConfig[item.role as UserRole] = {
                      name: item.name,
                      description: item.description || "",
                      permissions: (item.permissions || []) as Permission[]
                  };
              });
              setRolesConfig((prev: RolesConfig) => ({ ...prev, ...loadedConfig }));
          }

        } catch (e) {
            console.error("Failed to fetch user data:", e);
            setRealUserRoles([]);
            setUserPreferences(defaultPreferences);
        } finally {
            setIsLoadingRole(false);
        }
      };

      fetchUserData();
    } else {
      setRealUserRoles([]);
      setSimulatedUserRoles(null);
      setUserPreferences(defaultPreferences);
      setRolesConfig(ROLES_CONFIG); // Reseta para o fallback padrão estático
      setIsLoadingRole(false);
    }
  }, [currentUser, supabase]);


  const login = async (credentials: { email: string; password: string }) => {
    setSimulatedUserRoles(null);
    const { error } = await supabase.auth.signInWithPassword(credentials);
    return { error };
  };

  const signUp = async (credentials: {email: string, password: string}) => {
    const { data, error } = await supabase.auth.signUp(credentials);
    return { error, user: data.user };
  };
  
  const sendPasswordResetEmail = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/update-password`,
    });
    return { error };
  };
  
  const updateUserPassword = async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    if (!error) {
        await supabase.auth.signOut();
    }
    return { error };
  };

  const serverUpdateUserPreferences = useCallback(async (newPreferences: UserPreferences) => {
      await updateUserPreferences(newPreferences);
  }, []);

  const handleUpdateUserPreferences = useCallback(async (newPreferences: Partial<UserPreferences>) => {
    const updatedPrefs = { 
        ...userPreferences, 
        ...newPreferences,
        session: {
            ...userPreferences.session,
            ...newPreferences.session,
        },
        dashboard: {
          ...userPreferences.dashboard,
          ...newPreferences.dashboard,
          summary: {
            ...userPreferences.dashboard.summary,
            ...newPreferences.dashboard?.summary,
          },
          shortcuts: {
            ...userPreferences.dashboard.shortcuts,
            ...newPreferences.dashboard?.shortcuts,
          },
          analytics: {
            ...userPreferences.dashboard.analytics,
            ...newPreferences.dashboard?.analytics,
          },
        }
    };
    setUserPreferences(updatedPrefs);
    await serverUpdateUserPreferences(updatedPrefs);
  }, [userPreferences, serverUpdateUserPreferences]);

  const handleUpdateRolesConfig = async (newConfig: RolesConfig): Promise<{ success: boolean; error?: string }> => {
    const result = await saveRolesPermissions(newConfig);
    if (result.success) {
      setRolesConfig(newConfig);
    }
    return result;
  };

  const hasPermission = useCallback((permissionToCheck?: Permission): boolean => {
    if (!permissionToCheck) return true;
    if (isLoading || isLoadingRole || currentUserRoles.length === 0) return false;

    return currentUserRoles.some(role => {
      const roleConfig = rolesConfig[role];
      return roleConfig?.permissions.includes(permissionToCheck) ?? false;
    });
  }, [currentUserRoles, isLoading, isLoadingRole, rolesConfig]);
  
  const value: AuthContextType = {
    supabase,
    currentUser,
    currentUserRoles,
    realUserRoles,
    userPreferences,
    rolesConfig,
    isLoading,
    isLoadingRole,
    isSimulated,
    login,
    logout,
    signUp,
    sendPasswordResetEmail,
    updateUserPassword,
    setSimulatedUserRoles,
    clearSimulatedUserRoles: () => setSimulatedUserRoles(null),
    updateUserPreferences: handleUpdateUserPreferences,
    updateRolesConfig: handleUpdateRolesConfig,
    hasPermission,
    availableRoles: USER_ROLES_AVAILABLE.map(role => ({ value: role, label: ROLES_CONFIG[role]?.name || role })),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
